import React, { createContext, useContext, useEffect, useState } from "react";
import { signIn, signOut, signUp, confirmSignUp, confirmSignIn, resetPassword, confirmResetPassword, fetchAuthSession, fetchUserAttributes, getCurrentUser } from '@aws-amplify/auth';

interface UseAuth {
    isLoading: boolean;
    isAuthenticated: boolean;
    username: string;
    accessToken: string;
    handleSignIn: (username: string, password: string) => Promise<Result>;
    handleSignOut: () => void;
    handleSignUp: (email: string, password: string, name: string) => Promise<Result>;
    handleConfirmSignUp: (email: string, code: string) => Promise<Result>;
    handleConfirmNewPassword: (newPassword: string) => Promise<Result>;
    handleResetPassword: (username: string) => Promise<Result>;
    handleConfirmResetPassword: (username: string, confirmationCode: string, newPassword: string) => Promise<Result>;
}

interface Result {
    success: boolean;
    message: string;
}

type Props = {
    children?: React.ReactNode;
};

const authContext = createContext({} as UseAuth);

export const ProvideAuth: React.FC<Props> = ({ children }) => {
    const auth = useProvideAuth();
    return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = () => {
    return useContext(authContext);
};

const useProvideAuth = (): UseAuth => {
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [username, setUsername] = useState("");
    const [accessToken, setAccessToken] = useState("");

    useEffect(() => {
        fetchAuthSession()
            .then(
                (session) => {
                    setAccessToken(session.tokens?.idToken?.toString() ?? "");
                    // console.log("[DEBUG] accessToken: " + session?.tokens?.idToken?.toString());
                }
            ).catch((e) => {
                console.log("[DEBUG] Exception in fetchAuthSession:" + e.message);
                setUsername("");
                setIsAuthenticated(false);
                setIsLoading(false);
            });
        
        getCurrentUser()
            .then(
                (result) => {
                    console.log("[DEBUG] User: " + result.username);
                    setIsAuthenticated(true);
                    setIsLoading(false);
                    setUsername(result.username);
                }
            ).catch((e) => {
                console.log("[DEBUG] Exception in getCurrentUser:" + e.message);
                setUsername("");
                setIsAuthenticated(false);
                setIsLoading(false);
            });
        

            
        fetchUserAttributes()
            .then((result) => {
                setUsername(result?.email ?? "");
                setIsAuthenticated(true);
                setIsLoading(false);
            })
            .catch((e) => {
                console.log("[DEBUG] Exception in fetchUserAttributes:" + e.message);
                setUsername("");
                setIsAuthenticated(false);
                setIsLoading(false);
            });
    }, []);

    const handleSignIn = async (username: string, password: string) => {
        try {
            const result = await signIn({ username, password });

            if (result.isSignedIn) {
                setUsername(username);
                setIsAuthenticated(true);
                setIsLoading(false);
                const session = await fetchAuthSession();
                setAccessToken(session.tokens?.idToken?.toString() || "");
                return { success: true, message: "" };
            }

            if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                return { success: false, message: "NEW_PASSWORD_REQUIRED" };
            }

            return { success: false, message: "Incorrect username or password." };
        } catch (error: any) {
            console.log("[DEBUG] Exception Signing in:", error?.message);
            setUsername("");
            setIsAuthenticated(false);
            setIsLoading(false);
            setAccessToken("");
            return {
                success: false,
                message: error?.message?.includes("Incorrect") ? "Incorrect username or password." : (error instanceof Error ? error.message : "Login failed"),
            };
        }
    };

    const handleConfirmNewPassword = async (newPassword: string) => {
        try {
            const result = await confirmSignIn({ challengeResponse: newPassword });
            if (result.isSignedIn) {
                const session = await fetchAuthSession();
                setAccessToken(session.tokens?.idToken?.toString() || "");
                const attrs = await fetchUserAttributes().catch(() => null);
                setUsername(attrs?.email ?? "");
                setIsAuthenticated(true);
                setIsLoading(false);
                return { success: true, message: "" };
            }
            return { success: false, message: "Could not complete sign in." };
        } catch (error: any) {
            console.log("[DEBUG] Exception in handleConfirmNewPassword:", error?.message);
            let errorMessage = "Could not set new password.";
            if (error?.name === "InvalidPasswordException") {
                errorMessage = "Password does not meet requirements.";
            } else if (error?.message) {
                errorMessage = error.message;
            }
            return { success: false, message: errorMessage };
        }
    };
    

    const handleSignOut = async () => {
        try {
            await signOut()
                .then((user) => {
                    console.log("[DEBUG] User is signed out");
                    setUsername("");
                    setIsAuthenticated(false);
                    setIsLoading(false);
                    setAccessToken("");
                    
                })
                .catch((e) => {
                    setUsername("");
                    setIsAuthenticated(false);
                    setIsLoading(false);
                    setAccessToken("");
                });
            
            return { success: true, message: "" };
        } catch (error) {
            return {
                success: false,
                message: "LOGOUT FAIL",
            };
        }
    };

    const handleSignUp = async (email: string, password: string, name: string) => {
        try {
            const { isSignUpComplete, userId, nextStep } = await signUp({
                username: email,
                password,
                options: {
                    userAttributes: {
                        email,
                        name
                    }
                }
            });
            
            console.log("[DEBUG] Sign up successful:", { isSignUpComplete, userId, nextStep });
            
            if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
                return { 
                    success: true, 
                    message: "CONFIRM_EMAIL" // Special code to indicate confirmation needed
                };
            }
            
            return { 
                success: true, 
                message: "Account created successfully!" 
            };
        } catch (error: any) {
            console.log("[DEBUG] Exception in handleSignUp:", error);
            let errorMessage = "Sign up failed";
            const rawMessage =
                typeof error?.message === "string"
                    ? error.message
                    : typeof error?.cause?.message === "string"
                        ? error.cause.message
                        : "";
            
            if (error.name === 'UsernameExistsException') {
                errorMessage = "An account with this email already exists";
            } else if (error.name === 'InvalidPasswordException') {
                errorMessage = "Password does not meet requirements";
            } else if (
                error?.name === "UserLambdaValidationException" ||
                rawMessage.includes("PreSignUp failed")
            ) {
                errorMessage = "Please use your work email to create an account.";
            } else if (rawMessage) {
                errorMessage = rawMessage;
            }
            
            return {
                success: false,
                message: errorMessage,
            };
        }
    };

    const handleConfirmSignUp = async (email: string, code: string) => {
        try {
            const { isSignUpComplete, nextStep } = await confirmSignUp({
                username: email,
                confirmationCode: code
            });
            
            console.log("[DEBUG] Confirmation successful:", { isSignUpComplete, nextStep });
            
            return { 
                success: true, 
                message: "Email confirmed! You can now sign in." 
            };
        } catch (error: any) {
            console.log("[DEBUG] Exception in handleConfirmSignUp:", error);
            let errorMessage = "Confirmation failed";
            
            if (error.name === 'CodeMismatchException') {
                errorMessage = "Invalid confirmation code";
            } else if (error.name === 'ExpiredCodeException') {
                errorMessage = "Confirmation code has expired";
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            return {
                success: false,
                message: errorMessage,
            };
        }
    };

    const handleResetPassword = async (username: string) => {
        try {
            const { nextStep } = await resetPassword({ username });
            if (nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
                return { success: true, message: "Check your email for the reset code." };
            }
            return { success: true, message: "Password reset initiated." };
        } catch (error: any) {
            console.log("[DEBUG] Exception in handleResetPassword:", error);
            let errorMessage = "Could not send reset code";
            if (error.name === 'UserNotFoundException') {
                errorMessage = "No account found with this email.";
            } else if (error.name === 'LimitExceededException') {
                errorMessage = "Too many attempts. Please try again later.";
            } else if (error?.message) {
                errorMessage = error.message;
            }
            return { success: false, message: errorMessage };
        }
    };

    const handleConfirmResetPassword = async (username: string, confirmationCode: string, newPassword: string) => {
        try {
            await confirmResetPassword({ username, confirmationCode, newPassword });
            return { success: true, message: "Password reset successfully. You can now sign in." };
        } catch (error: any) {
            console.log("[DEBUG] Exception in handleConfirmResetPassword:", error);
            let errorMessage = "Could not reset password";
            if (error.name === 'CodeMismatchException') {
                errorMessage = "Invalid or expired code. Please request a new one.";
            } else if (error.name === 'InvalidPasswordException') {
                errorMessage = "Password does not meet requirements.";
            } else if (error?.message) {
                errorMessage = error.message;
            }
            return { success: false, message: errorMessage };
        }
    };

    return {
        isLoading,
        isAuthenticated,
        username,
        accessToken,
        handleSignIn,
        handleSignOut,
        handleSignUp,
        handleConfirmSignUp,
        handleConfirmNewPassword,
        handleResetPassword,
        handleConfirmResetPassword,
    };
};
